import { CircularProgress, Stack, Typography } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import { Panel, PanelGroup } from "@poseidon/ui/components/Panel";
import config from "@poseidon/utils/config";
import { useEffect } from "react";
import NoteLog from "../../NoteLog/NoteLog";
import * as api from "../api";
import { useIdvNotesLog } from "../hooks/useIdvNotesLog";
import { useKycAmlNotesLog } from "../hooks/useKycAmlNotesLog";
import { useSailorRiskProfile } from "../hooks/useSailorRiskProfile";
import { useSailorContext } from "../SailorContext";
import { AmlChecks } from "../../AmlCheck/AmlChecks";
import { IdvChecks } from "../components/riskProfile/IdvChecks";
import { IdvStatus } from "../components/riskProfile/IdvStatus";
import { KycAmlPepResults } from "../components/riskProfile/KycAmlPepResults";
import { KycDesignation } from "../components/riskProfile/KycDesignation";
import { FileManagement } from "../../Files";
import { SailorCheckList } from "../../CheckList/components/SailorCheckList";
import { useParams } from "react-router-dom";

const { fileResolvers: filePurposes, noteCategories } = config;

export function SailorRiskProfileIdentity() {
  const { applicationId } = useParams();
  const sailor = useSailorContext();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const { sailorRiskProfile, loading, error } = useSailorRiskProfile({
    sailorId: sailor.id,
  });
  const { kycAmlNotesLog } = useKycAmlNotesLog(sailor.id);
  const { idvNotesLog } = useIdvNotesLog(sailor.id);

  const updateSailorRiskProfile = async (values) => {
    try {
      await api.updateSailorRiskProfile({ sailorId: sailor.id, values });
      setToast("Successfully updated Risk Profile for Sailor");
    } catch (error) {
      setError(error.message);
    }
  };

  const onKycAmlNotesAddHandler = (values, { resetForm }) => {
    try {
      api.addNote({
        ...values,
        sailor,
        noteCategory: noteCategories.kycAmlNote,
      });
      resetForm();
      setToast("Added KYC/AML Note");
    } catch (error) {
      setError(error.message);
    }
  };

  const onIdvNotesAddHandler = (values, { resetForm }) => {
    try {
      api.addNote({ ...values, sailor, noteCategory: noteCategories.idvNote });
      resetForm();
      setToast("Added ID&V Note");
    } catch (error) {
      setError(error.message);
    }
  };

  useEffect(() => {
    if (error) {
      setError(error);
    }
  });

  if (loading || !sailorRiskProfile) {
    return <CircularProgress sx={{ alignSelf: "center" }} />;
  }

  return (
    <Stack spacing={5} sx={{ px: 5, mb: 5 }}>
      <Typography variant="h5" component="h3">
        KYC/AML Checks
      </Typography>
      <PanelGroup>
        <SailorCheckList
          title="Prerequisites"
          sailor={sailor}
          applicationId={applicationId}
          target="kycAml"
        />
        <KycDesignation
          sailor={sailor}
          sailorRiskProfile={sailorRiskProfile}
          updateSailorRiskProfile={updateSailorRiskProfile}
        />
        <AmlChecks sailorId={sailor.id} />
        <KycAmlPepResults
          sailorRiskProfile={sailorRiskProfile}
          updateSailorRiskProfile={updateSailorRiskProfile}
        />
        <Panel
          title="Notes"
          collapsedDetail={`${kycAmlNotesLog.length} notes`}
          expandable
          startCollapsed
        >
          <NoteLog
            log={kycAmlNotesLog}
            onSubmitHandler={onKycAmlNotesAddHandler}
          />
        </Panel>
      </PanelGroup>
      <Typography variant="h5" component="h3">
        ID&amp;V
      </Typography>
      <PanelGroup>
        <SailorCheckList
          title="Prerequisites"
          sailor={sailor}
          applicationId={applicationId}
          target="idv"
        />

        <IdvChecks
          sailorId={sailor.id}
          riskProfileIdvCheckState={sailorRiskProfile.idvStatus}
        />
        <IdvStatus
          sailorRiskProfile={sailorRiskProfile}
          updateSailorRiskProfile={updateSailorRiskProfile}
        />
        <Panel
          title="Notes"
          collapsedDetail={`${idvNotesLog.length} notes`}
          expandable
          startCollapsed
        >
          <NoteLog log={idvNotesLog} onSubmitHandler={onIdvNotesAddHandler} />
        </Panel>
      </PanelGroup>
      <Panel title="Risk Profile Files" expandable>
        <FileManagement
          fileCategory="sailor"
          resolveMetadata={filePurposes.sailorRiskProfile}
          sailorId={sailor.id}
        />
      </Panel>
    </Stack>
  );
}

export default IdvStatus;
